// ─── Control Standards ─────────────────────────────────────────────────────────
//
// One chip for every filter, menu and picker trigger in the app.
//
// Before this there were twelve separate declarations of "a 36px pill with a border
// and a chevron", and they had drifted into two visibly different controls: the
// project Tickets page asked for `text-sm font-medium`, everything else for
// `text-[13px] font-semibold`.
//
// That reads as a far bigger difference than 13-vs-14px suggests, because Lato ships
// 300/400/700/900 and nothing between — a requested 500 falls back to 400 and a
// requested 600 rounds up to 700. So the two rows were not a pixel apart, they were
// REGULAR against BOLD, side by side, doing the same job.
//
// The Tickets page's lighter chip won. Import these rather than retyping them:
//
//   <button :class="[CONTROL_CHIP, n ? CONTROL_CHIP_SET : CONTROL_CHIP_IDLE]">
//     Priority<span v-if="n" :class="CONTROL_COUNT">· {{ n }}</span>
//     <ChevronDown class="w-3.5 h-3.5 text-slate-400" />
//   </button>
//
// `font-medium` renders at 400 today. It is written as medium rather than normal
// because 500 is the weight the control is asking for; if Lato 500 is ever loaded,
// these chips pick it up together, which is the point of them sharing a constant.
//
// The ScopeBar is deliberately NOT on this chip: it stays h-8 and semibold so the
// scope tier reads as one level above the filters it governs.

export const CONTROL_CHIP      = 'inline-flex items-center gap-2 h-9 px-3 rounded-lg border text-sm font-medium whitespace-nowrap cursor-pointer transition-colors'
export const CONTROL_CHIP_IDLE = 'border-gray-200 bg-white text-slate-700 hover:bg-slate-50'
export const CONTROL_CHIP_SET  = 'border-blue-200 bg-blue-50 text-blue-800'
/**
 * The same control as an icon alone: the chip's height and border, square. For an action that
 * is on the toolbar but seldom used, such as Export (Kenneth, 25 Sep: "not really used
 * always"). It has no words, so it always carries a `title` and an `aria-label`. Pair with
 * CONTROL_CHIP_IDLE for its colours.
 */
export const CONTROL_ICON_CHIP = 'inline-flex items-center justify-center h-9 w-9 shrink-0 rounded-lg border cursor-pointer transition-colors'

// The selected-count that trails a chip's label. Rendered as "· 3", not a pill: at
// this weight a filled badge shouts over the label it belongs to.
export const CONTROL_COUNT = 'text-xs font-bold'

// A row inside an open chip panel.
export const CONTROL_ITEM = 'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap text-left'

// "Clear filters" and "Clear scope": the link that puts a narrowed list back, shown only
// while something is narrowed. Blue text, no box and no icon - one look for both, because
// they do the same thing to different rows (Kenneth, 25 Sep). Clear scope was a grey
// "x Clear scope" that read as disabled beside the blue-bordered scope chip it clears.
//
// self-stretch rather than a height: the scope row is h-8 and the filter rows h-9, and
// the link takes the height of whichever row it sits in, so the whole row height is
// clickable. ClearLinkIsSharedTest keeps every Clear filters / Clear scope button on this.
export const CLEAR_LINK = 'inline-flex items-center self-stretch px-1 text-[13px] text-blue-800 hover:underline cursor-pointer whitespace-nowrap'

// ─── Navigation ───────────────────────────────────────────────────────────────
//
// One "you are here" for every nav in the app.
//
// Six navigation components had four visually distinct answers, which is the single
// clearest reason the modules read as separate products:
//
//   AppSidebar, PortalSidebar       bg-blue-50   text-blue-700   pale blue
//   MeetingsSubNav                  bg-blue-50   text-blue-600   same fill, other token
//   AdminSidebar, CompanySidebar    bg-slate-100 text-slate-900  grey
//   ProjectManagementSubNav         bg-indigo-50 text-indigo-700 a hue not in the palette
//
// Two of those differed only by which blue token the label used, which is drift rather
// than a decision. The pale-blue treatment won because it is what the two primary
// sidebars already do and it reads as selection rather than as hover.
//
// text-blue-800 for the same reason the pills use it: blue-700 doubles as the button
// hover fill, so the readable value lives on the dark step. 6.16:1 against the fill.
export const NAV_ITEM        = 'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors'
export const NAV_ITEM_IDLE   = 'text-slate-700 hover:bg-slate-50'
export const NAV_ITEM_ACTIVE = 'bg-blue-50 text-blue-800 font-bold'

// The row the state above is painted on.
//
// Sharing only the active colour was not enough: the same six navs still drew three
// different rows underneath it, which is what Kenneth saw on Super Admin —
//
//   AppSidebar, PortalSidebar       px-3 py-2, mb-0.5 between rows, idle text-slate-700
//   AdminSidebar, CompanySidebar    px-[10px] h-8, NO gap between rows, idle text-slate-500,
//                                   and icons coloured by hand at slate-400 / blue-600
//   MeetingsSubNav                  px-2.5 h-9, idle text-slate-600
//
// So on Super Admin the hovered row and the selected row touched, with no space between
// them, and both sat lighter than the same rows in Planning & Delivery. Every difference
// there is a notation difference or an oversight, not a decision: gap-[10px] IS gap-2.5
// and rounded-[6px] IS rounded-md, written twice by people who could not see each other.
//
// The icon takes no colour of its own — it inherits the row's, so active is one blue and
// idle is one slate, and a nav cannot end up with a blue label beside a grey glyph.
// h-9 rather than vertical padding, so the row is 36px whether or not it is drawing a
// label. With `py-2` its height came from the label's line-height — 35.5px expanded
// against 32px collapsed — so collapsing lifted every icon, and by more the further down
// the list you looked: the last one moved 45px. A row that stops showing its label should
// not change size.
export const NAV_ROW         = 'flex items-center gap-2.5 h-9 px-3 rounded-md text-[13px] font-medium cursor-pointer transition-colors mb-0.5'
export const NAV_ROW_IDLE    = 'text-slate-700 hover:bg-slate-100'

// A collapsed row keeps the SAME left padding as an expanded one — it just stops drawing
// its label. It used to centre the glyph instead, and because the rail's width animates
// over 200ms while `justify-content: center` applies instantly, every icon shot right and
// slid back: measured at 28px, then 120px, then back to 28. The rail is 56px wide and the
// row's 12px padding puts a 16px glyph dead centre anyway, so centring bought nothing and
// cost the animation.
//
// An inline style rather than a class, because `px-2` and the row's own `px-3` are the
// same specificity and which one wins depends on the order Tailwind happens to emit them.
export const NAV_ROW_COLLAPSED = 'padding-left: 12px; padding-right: 12px'

// The same selection, on a surface that is not a nav row.
//
// A switcher panel — modules, companies, workspaces, projects, Hive spaces — lists
// two-line rows with an avatar, so it cannot use NAV_ROW's geometry. What it must share is
// the *mark*: before this, being in a module was shown by a green tick and nothing else,
// so the row you were on looked exactly like the fourteen you were not, and the only
// colour on the panel was a tick in a hue the selection language does not otherwise use.
//
// Same value as NAV_ITEM_ACTIVE, named separately so the nav guard can stay strict about
// which rows are allowed to carry their own geometry.
export const MENU_ROW_ACTIVE = 'bg-blue-50 text-blue-800 font-bold'

// ─── Form fields ──────────────────────────────────────────────────────────────
//
// The box a value is typed or chosen in, inside a modal or a form.
//
// This is the shape the app already converged on by hand — 34px tall, 10px of side
// padding, a gray-200 hairline that turns blue on focus — written out in full in about
// ninety places, with a dozen near-misses at 36px, 9px padding, slate-200, or a ring
// instead of a border.
//
// A native <select> cannot be styled this way past its closed state: the list it opens is
// drawn by the operating system, in the OS font, ignoring every one of these values. That
// is why the pickers in Kenneth's screenshots look like they came from a different
// application than the panel around them. Base/SelectMenu with `variant="field"` is the
// replacement — it uses this same token for its trigger, so the two sit level in a form.
/**
 * The field without its height, for a control that has to line up with a taller
 * neighbour. DatePicker's block mode is h-10, and 34px beside 40px in one row is the
 * same 6px mismatch that made Project Settings' Add button look misaligned.
 */
/**
 * The search box that sits in a filter row.
 *
 * There was no constant for this and it showed: six treatments across the app — heights of
 * 32, 34 and 36px, `rounded-md` and `rounded-lg`, `text-sm` and `text-[13px]`, focus rings
 * on blue-400 and blue-500, widths of w-52, w-56, w-64, max-w-sm and full, and some with a
 * white ground and some inheriting the page's.
 *
 * h-9 and rounded-lg, because a search box lives NEXT TO filter chips and CONTROL_CHIP is
 * h-9 rounded-lg. Matching FIELD's 34px instead left it 2px short of the control beside it,
 * which is the same mismatch that made the Project Settings toolbar look wrong.
 *
 * The width is deliberately not here: it is the one thing that genuinely differs by page,
 * and baking w-64 in would be a worse lie than leaving it to the caller.
 */
export const SEARCH_FIELD = 'h-9 pl-9 pr-3 rounded-lg border border-gray-200 bg-white text-[13px] text-slate-800 outline-none transition-colors focus:border-blue-500 placeholder:text-slate-400'

/** The glass inside it. Centred on h-9: (36 - 16) / 2 = 10px. */
export const SEARCH_ICON = 'w-4 h-4 absolute left-3 top-2.5 text-slate-400 pointer-events-none'

/**
 * The action button — a header's "Invite user", a form's "Save".
 *
 * There was no constant, so 60 files hand-rolled this exact string and two pages
 * (Company Information, Company Signature) instead wrote a PRIVATE `.btn-primary` into a
 * scoped <style> at height 38px and radius 9px. Those shadowed the global class, so the
 * Save buttons measured 38px beside a 34px "Invite user" reached from the same sidebar —
 * and no amount of reading app.css explained it, because the rule was in the page.
 *
 * 34px to match FIELD, not the h-9 of CONTROL_CHIP: a button in a header sits beside other
 * buttons and above a form, never in a filter row.
 *
 * The disabled state is part of the constant. Both private copies carried `opacity: .5`,
 * and a Save that stays fully lit while disabled until dirty is a worse bug than the height.
 */
export const BTN_BASE = 'inline-flex items-center gap-1.5 h-[34px] px-[14px] rounded-md text-[13px] font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed'
export const BTN_PRIMARY = BTN_BASE + ' bg-blue-600 text-white border border-blue-600 hover:bg-blue-700'
export const BTN_SECONDARY = BTN_BASE + ' bg-white text-slate-700 border border-gray-200 hover:bg-slate-50'

export const FIELD_BASE = 'w-full px-[10px] rounded-md border border-gray-200 bg-white text-[13px] text-slate-800 outline-none transition-colors focus:border-blue-500'
export const FIELD = FIELD_BASE + ' h-[34px]'
export const FIELD_LABEL = 'block text-[12px] font-medium text-slate-600 mb-1'
